import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, formatPhoneNumber } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ChevronLeft,
  Send,
  DollarSign,
  Ban,
  Pencil,
  Trash2,
  Copy,
  Building2,
  MapPin,
  CreditCard,
  Mail,
  BellRing,
  Loader2,
  Repeat,
  ArrowRight,
} from 'lucide-react';
import { MakeRecurringDialog } from './make-recurring-dialog';

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'destructive',
  cancelled: 'secondary',
  void: 'secondary',
};

interface InvoiceDetail {
  id: string;
  invoiceNumber: number;
  businessProfileId: string;
  businessProfileName: string;
  bpAddressLine1: string | null;
  bpAddressLine2: string | null;
  bpCity: string | null;
  bpState: string | null;
  bpZip: string | null;
  bpCountry: string | null;
  bpPhone: string | null;
  bpEmail: string | null;
  bpLogoUrl: string | null;
  bpTaxId: string | null;
  accountId: string;
  accountName: string;
  accountPhone: string | null;
  accountAddressLine1: string | null;
  accountAddressLine2: string | null;
  accountCity: string | null;
  accountState: string | null;
  accountPostalCode: string | null;
  accountCountry: string | null;
  accountContactEmail: string | null;
  accountContactName: string | null;
  applicationName: string | null;
  recurringScheduleId: string | null;
  scheduleName: string | null;
  status: string;
  issueDate: string;
  dueDate: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  amountPaid: number;
  notes: string | null;
  paymentTerms: string | null;
  items: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
    itemType: string;
  }[];
  payments: {
    id: string;
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    referenceNumber: string | null;
    notes: string | null;
  }[];
}

export function InvoiceDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'check',
    referenceNumber: '',
    notes: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const result = await api.getInvoice(id!);
      return result.data as InvoiceDetail;
    },
    enabled: Boolean(id),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => api.updateInvoiceStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
    },
  });

  const paymentMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createPayment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
      setPaymentDialogOpen(false);
      setPaymentForm({
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'check',
        referenceNumber: '',
        notes: '',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteInvoice(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate('/billing/invoices');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => api.duplicateInvoice(id!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      const newId = (result as { data?: { id?: string } })?.data?.id;
      if (newId) navigate(`/billing/invoices/${newId}/edit`);
    },
    onError: () => {
      alert('Failed to duplicate invoice. Please try again.');
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      // Auto-mark as sent if currently a draft
      if (data?.status === 'draft') {
        await api.updateInvoiceStatus(id!, 'sent');
      }
      return api.sendInvoiceEmail(id!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      alert('Invoice email sent successfully!');
    },
    onError: () => {
      alert('Failed to send email. Please try again.');
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: () => api.sendPaymentReminder(id!),
    onSuccess: () => {
      alert('Payment reminder sent successfully!');
    },
    onError: () => {
      alert('Failed to send reminder. Please try again.');
    },
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string) => api.deletePayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['finance-dashboard-summary'] });
    },
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const invoice = data;
  const balanceDue = invoice.total - invoice.amountPaid;

  function handleRecordPayment(e: React.FormEvent) {
    e.preventDefault();
    paymentMutation.mutate({
      invoiceId: id,
      amount: parseFloat(paymentForm.amount),
      paymentDate: paymentForm.paymentDate || null,
      paymentMethod: paymentForm.paymentMethod,
      referenceNumber: paymentForm.referenceNumber || null,
      notes: paymentForm.notes || null,
    });
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/billing/invoices')}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Invoices
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Invoice #{invoice.invoiceNumber}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={statusVariant[invoice.status] || 'secondary'}>
                {invoice.status}
              </Badge>
              {invoice.recurringScheduleId && (
                <Badge variant="outline" className="gap-1">
                  <Repeat className="h-3 w-3" />
                  Recurring
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {(invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'partial' || invoice.status === 'overdue') && (
            <Button
              variant="outline"
              onClick={() => navigate(`/billing/invoices/${id}/edit`)}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => duplicateMutation.mutate()}
            disabled={duplicateMutation.isPending}
          >
            {duplicateMutation.isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Copy className="h-4 w-4 mr-2" />}
            Duplicate
          </Button>
          {!invoice.recurringScheduleId && invoice.status !== 'void' && invoice.status !== 'cancelled' && (
            <Button variant="outline" onClick={() => setRecurringDialogOpen(true)}>
              <Repeat className="h-4 w-4 mr-2" />
              Make Recurring
            </Button>
          )}
          {invoice.status === 'draft' && (
            <>
              <Button onClick={() => statusMutation.mutate('sent')}>
                <Send className="h-4 w-4 mr-2" />
                Mark as Sent
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (confirm('Delete this draft invoice?')) deleteMutation.mutate();
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          )}
          {(invoice.status === 'sent' || invoice.status === 'partial' || invoice.status === 'overdue') && (
            <>
              <Button onClick={() => {
                setPaymentForm(f => ({ ...f, amount: balanceDue.toString() }));
                setPaymentDialogOpen(true);
              }}>
                <DollarSign className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
              <Button
                variant="outline"
                onClick={() => sendReminderMutation.mutate()}
                disabled={sendReminderMutation.isPending}
              >
                {sendReminderMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BellRing className="h-4 w-4 mr-2" />}
                Send Reminder
              </Button>
              <Button variant="outline" onClick={() => statusMutation.mutate('void')}>
                <Ban className="h-4 w-4 mr-2" />
                Void
              </Button>
            </>
          )}
          {invoice.status !== 'paid' && invoice.status !== 'void' && invoice.status !== 'cancelled' && (
            <Button
              variant="outline"
              onClick={() => sendEmailMutation.mutate()}
              disabled={sendEmailMutation.isPending}
            >
              {sendEmailMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {invoice.status === 'draft' ? 'Send & Email' : 'Email Invoice'}
            </Button>
          )}
        </div>
      </div>

      {/* Recurring provenance */}
      {invoice.recurringScheduleId && (
        <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg bg-muted/50 border text-sm">
          <div className="flex items-center gap-2">
            <Repeat className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>
              Part of the recurring schedule{' '}
              <strong>{invoice.scheduleName || 'Recurring billing'}</strong>. Editing the schedule
              changes future invoices, not this one.
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => navigate(`/billing/recurring/${invoice.recurringScheduleId}`)}
          >
            Manage schedule
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Email Recipient Notice */}
      {invoice.status !== 'paid' && invoice.status !== 'void' && invoice.status !== 'cancelled' && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
          <Mail className="h-4 w-4 text-blue-600 shrink-0" />
          {invoice.accountContactEmail ? (
            <span className="text-blue-900">
              Invoices will be emailed to: <strong>{invoice.accountContactName || 'Primary Contact'}</strong> &lt;{invoice.accountContactEmail}&gt;
            </span>
          ) : (
            <span className="text-amber-800 bg-amber-50 border-amber-200">
              No email address on file for this account. Please add a contact with an email to send invoices.
            </span>
          )}
        </div>
      )}

      {/* Invoice Document */}
      <Card>
        <CardContent className="p-8">
          {/* From / To */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">From</span>
              </div>
              {invoice.bpLogoUrl && (
                <img
                  src={invoice.bpLogoUrl}
                  alt={`${invoice.businessProfileName} logo`}
                  className="h-12 max-w-[200px] object-contain mb-2"
                />
              )}
              <h3 className="text-lg font-bold">{invoice.businessProfileName}</h3>
              {invoice.bpAddressLine1 && <p className="text-sm">{invoice.bpAddressLine1}</p>}
              {invoice.bpAddressLine2 && <p className="text-sm">{invoice.bpAddressLine2}</p>}
              {(invoice.bpCity || invoice.bpState) && (
                <p className="text-sm">
                  {[invoice.bpCity, invoice.bpState].filter(Boolean).join(', ')}
                  {invoice.bpZip && ` ${invoice.bpZip}`}
                </p>
              )}
              {invoice.bpPhone && <p className="text-sm">{formatPhoneNumber(invoice.bpPhone)}</p>}
              {invoice.bpEmail && <p className="text-sm">{invoice.bpEmail}</p>}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Bill To</span>
              </div>
              <h3 className="text-lg font-bold">{invoice.accountName}</h3>
              {invoice.accountContactName && (
                <p className="text-sm">Attn: {invoice.accountContactName}</p>
              )}
              {invoice.applicationName && (
                <p className="text-sm text-muted-foreground">
                  Application: {invoice.applicationName}
                </p>
              )}
              {invoice.accountAddressLine1 ? (
                <>
                  <p className="text-sm">{invoice.accountAddressLine1}</p>
                  {invoice.accountAddressLine2 && <p className="text-sm">{invoice.accountAddressLine2}</p>}
                  <p className="text-sm">
                    {[invoice.accountCity, invoice.accountState].filter(Boolean).join(', ')}
                    {invoice.accountPostalCode && ` ${invoice.accountPostalCode}`}
                  </p>
                  {invoice.accountCountry && invoice.accountCountry !== 'USA' && (
                    <p className="text-sm">{invoice.accountCountry}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">No address on file</p>
              )}
              <p className="text-sm mt-1">
                {invoice.accountPhone
                  ? formatPhoneNumber(invoice.accountPhone)
                  : <span className="text-muted-foreground italic">No phone on file</span>}
              </p>
              <p className="text-sm">
                {invoice.accountContactEmail
                  ? invoice.accountContactEmail
                  : <span className="text-muted-foreground italic">No email on file</span>}
              </p>
            </div>
          </div>

          {/* Invoice Meta */}
          <div className="grid grid-cols-4 gap-4 mb-8 p-4 bg-muted/50 rounded-lg">
            <div>
              <div className="text-xs text-muted-foreground">Invoice #</div>
              <div className="font-mono font-bold">{invoice.invoiceNumber}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Issue Date</div>
              <div className="font-medium">{formatDate(invoice.issueDate)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Due Date</div>
              <div className="font-medium">{formatDate(invoice.dueDate)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Payment Terms</div>
              <div className="font-medium">{invoice.paymentTerms || 'Net 30'}</div>
            </div>
          </div>

          <Separator className="mb-6" />

          {/* Line Items */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.description}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{item.itemType}</Badge>
                  </TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.unitPrice)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} className="text-right">Subtotal</TableCell>
                <TableCell className="text-right">{formatCurrency(invoice.subtotal)}</TableCell>
              </TableRow>
              {invoice.taxRate > 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-right">
                    Tax ({(invoice.taxRate * 100).toFixed(2)}%)
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(invoice.taxAmount)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={4} className="text-right text-lg font-bold">Total</TableCell>
                <TableCell className="text-right text-lg font-bold">
                  {formatCurrency(invoice.total)}
                </TableCell>
              </TableRow>
              {invoice.amountPaid > 0 && (
                <>
                  <TableRow>
                    <TableCell colSpan={4} className="text-right text-green-600">
                      Amount Paid
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      -{formatCurrency(invoice.amountPaid)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-bold">
                      Balance Due
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {formatCurrency(balanceDue)}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableFooter>
          </Table>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <div className="text-sm font-medium text-muted-foreground mb-1">Notes</div>
              <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      {invoice.payments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoice.payments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <div className="font-medium">{formatCurrency(payment.amount)}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(payment.paymentDate)} via{' '}
                      <span className="capitalize">{payment.paymentMethod.replace('_', ' ')}</span>
                      {payment.referenceNumber && ` - Ref: ${payment.referenceNumber}`}
                    </div>
                    {payment.notes && (
                      <div className="text-xs text-muted-foreground mt-1">{payment.notes}</div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => {
                      if (confirm('Delete this payment?')) {
                        deletePaymentMutation.mutate(payment.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Make Recurring Dialog */}
      <MakeRecurringDialog
        open={recurringDialogOpen}
        onOpenChange={setRecurringDialogOpen}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        accountName={invoice.accountName}
        issueDate={invoice.issueDate}
        total={invoice.total}
        itemCount={invoice.items.length}
        paymentTerms={invoice.paymentTerms}
      />

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Balance due: {formatCurrency(balanceDue)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                />
              </div>
              <div>
                <Label>Method</Label>
                <Select
                  value={paymentForm.paymentMethod}
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, paymentMethod: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="wire">Wire</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input
                value={paymentForm.referenceNumber}
                onChange={(e) => setPaymentForm({ ...paymentForm, referenceNumber: e.target.value })}
                placeholder="Check #, transaction ID, etc."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={paymentMutation.isPending}>
                {paymentMutation.isPending ? 'Recording...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
